import {
  InputAdornment,
  TextField as MuiTextField,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme
} from '@mui/material';
import { FieldProps, TextFieldSchema } from '../types/Form';
import { FieldWrapper } from './FieldWrapper';

const getAIGradientVariantSxProps = (theme: Theme): SxProps => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    border: '1px solid transparent',
    borderImage: `${theme.palette.aiGradient} 1`,
    '& fieldset': {
      border: 'none'
    },
    '&:hover fieldset': {
      border: 'none'
    },
    '&.Mui-focused fieldset': {
      border: 'none'
    }
  }
});

export type TextFieldProps = FieldProps<TextFieldSchema>;
export const TextField: React.FC<TextFieldProps> = ({
  name,
  value,
  field,
  errorMessage,
  onChange,
  onBlur
}) => {
  const theme = useTheme();
  const {
    label,
    subLabel,
    containerSx,
    type = 'text',
    variant = 'outlined',
    rows = 5,
    startIcon,
    endIcon,
    autoFocus,
    maxCharacters,
    onKeyDown,
    ...restField
  } = field;

  return (
    <FieldWrapper
      label={label}
      subLabel={subLabel}
      errorMessage={errorMessage}
      sx={containerSx}
    >
      <MuiTextField
        name={name}
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        error={!!errorMessage}
        type={type === 'textarea' ? 'text' : type}
        multiline={type === 'textarea'}
        rows={type === 'textarea' ? rows : undefined}
        autoFocus={autoFocus}
        onFocus={(event) => {
          event.preventDefault();
          const value = event.target.value;
          // Set cursor at the end of the input
          event.target.setSelectionRange(value.length, value.length);
        }}
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ) : undefined,
          endAdornment: endIcon ? (
            <InputAdornment position="end">{endIcon}</InputAdornment>
          ) : undefined
        }}
        inputProps={{
          'data-testid': `text-field-${name}`
        }}
        {...restField}
        variant="outlined"
        sx={
          {
            ...(variant === 'aiGradient'
              ? getAIGradientVariantSxProps(theme)
              : {}),
            ...(restField.sx || {})
          } as SxProps
        }
      />
      {maxCharacters && (
        <Stack direction="row" justifyContent="flex-end" mt={1}>
          <Typography variant="body2">
            {value.length}/{maxCharacters} characters remaining
          </Typography>
        </Stack>
      )}
    </FieldWrapper>
  );
};
